import React, { PropsWithChildren, forwardRef, useImperativeHandle, useState } from 'react';

import { Radio, Text } from '@rbilabs/universal-components';
import { StoreChangeDisclaimer } from 'features/store/pages/pickup-store-selector/components';
import { useIntl } from 'react-intl';
import { FlatList } from 'react-native';
import { View } from 'react-native';

import ActionButton, { ActionButtonVariants } from 'components/action-button';
import Dialog from 'components/dialog';
import { IsoCountryCode } from 'generated/graphql-gateway';
import { IRemoveDeliveryAddressInput } from 'generated/graphql-gateway';
import { LogUserErrorSeverity, logUserErrorMessage } from 'hooks/log-user-error-message';
import useErrorModal from 'hooks/use-error-modal';
import { CustomEventNames, EventTypes, logEvent } from 'state/amplitude';
import { isWeb } from 'utils/environment';
import { IPlaceAddress } from 'utils/geolocation';

import { DeliveryTerms } from '../delivery-terms';
import { DialogStyles } from '../new-address-form/styled';

import { DeliveryAddressItem } from './delivery-address-item';
import { ButtonContainer, DeliveryInfoContainer } from './styled';
import { IDeliveryAddressesProps } from './types';

const castCountryType = (country: IsoCountryCode | null | undefined): string | undefined =>
  (country && (IsoCountryCode[country] as string)) || undefined;

export interface DeliveryAddressSelectHandle {
  onSelectAddress: () => void;
}

export const DeliveryAddresses = forwardRef<
  DeliveryAddressSelectHandle,
  PropsWithChildren<IDeliveryAddressesProps>
>(
  (
    {
      addresses,
      onCreate,
      selected,
      setSelected,
      setAddress,
      isLoading,
      removeDeliveryAddress,
      onEditAddress,
      setShowTermsView,
    },
    ref
  ) => {
    const { formatMessage } = useIntl();

    const [ErrorDialog, openErrorDialog] = useErrorModal({
      modalAppearanceEventMessage: 'Error Removing Delivery Address',
    });

    const [showRemoveDialog, setShowRemoveDialog] = useState(false);
    const [dialogData, setDialogData] = useState<IRemoveDeliveryAddressInput | null>(null);

    const openRemoveAddressDialog = (data: IRemoveDeliveryAddressInput) => {
      setDialogData(data);
      setShowRemoveDialog(true);
    };

    const handleRemoveAddress = (address: IRemoveDeliveryAddressInput) => {
      openRemoveAddressDialog(address);
    };

    const handleConfirmRemove = async () => {
      if (!dialogData) {
        return;
      }
      try {
        await removeDeliveryAddress({
          addressLine1: dialogData.addressLine1,
          addressLine2: dialogData.addressLine2 || '',
          city: dialogData.city,
          phoneNumber: dialogData.phoneNumber || '',
        });
        setShowRemoveDialog(false);
      } catch (error) {
        logUserErrorMessage({
          message: 'Remove delivery address failed',
          severity: LogUserErrorSeverity.Recoverable,
          context: {
            error,
            address: dialogData.addressLine1,
          },
        });

        openErrorDialog({
          title: formatMessage({ id: 'errorRemovingAddress' }),
          body: formatMessage({
            id: 'somethingWentWrongRemovingAddress',
            defaultMessage: 'Something went wrong while removing the address. Please try again.',
          }),
          error: error as Error | undefined,
        });
      }
    };

    const onSelectAddress = () => {
      const address = addresses[+selected];
      logEvent(CustomEventNames.SELECT_DELIVERY_SAVED_ADDRESS, EventTypes.Other);

      const selectedAddressComponents: IPlaceAddress = {
        addressLine1: address.addressLine1,
        addressLine2: address.addressLine2 || '',
        alias: address.alias || '',
        city: address.city,
        defaultInstruction: address.defaultInstruction || '',
        state: address.state || '',
        streetNumber: address.streetNumber || '',
        route: address.route || '',
        zip: address.zip || '',
        country: castCountryType(address.country),
        phoneNumber: address.phoneNumber || '',
      };

      let addressCoordinates;
      if (address.latitude && address.longitude) {
        addressCoordinates = {
          lat: address.latitude,
          lng: address.longitude,
        };
      }

      const selectedPhoneNumber = address.phoneNumber;
      setAddress({
        address: selectedAddressComponents,
        phoneNumber: selectedPhoneNumber,
        coordinates: addressCoordinates,
      });
    };

    useImperativeHandle(ref, () => ({
      onSelectAddress,
    }));

    const DeliveryAddressesButtons = () => (
      <ButtonContainer>
        <ActionButton
          testID="create-delivery-address"
          disabled={isLoading}
          onPress={onCreate}
          fullWidth
          variant={ActionButtonVariants.OUTLINE}
        >
          {formatMessage({ id: 'deliveryAddNewAddress' })}
        </ActionButton>
        <DeliveryInfoContainer>
          <DeliveryTerms />
          <StoreChangeDisclaimer />
        </DeliveryInfoContainer>
      </ButtonContainer>
    );

    const RadioGroup: React.FC<React.PropsWithChildren> = ({ children }) => (
      <Radio.Group
        name="delivery-addresses-radio"
        testID="delivery-addresses"
        alignItems="stretch"
        value={selected.toString()}
        onChange={value => setSelected(value)}
        focusable={!isWeb} // Prevents focus on web, which causes the page to scroll to the focused element.
      >
        {children}
      </Radio.Group>
    );
    return (
      <>
        <RadioGroup>
          <FlatList
            data={addresses}
            keyExtractor={(_, index) => index.toString()}
            renderItem={({ item, index }) => (
              <DeliveryAddressItem
                address={item}
                value={index.toString()}
                onEdit={() => onEditAddress?.(item)}
                onRemove={() => handleRemoveAddress(item)}
              />
            )}
            ListFooterComponent={() => <DeliveryAddressesButtons />}
          />
        </RadioGroup>

        {showRemoveDialog && dialogData && (
          <Dialog
            showDialog={showRemoveDialog}
            headingComponent={
              <View style={DialogStyles.headingContainer}>
                <Text variant="headerOne" textAlign="center">
                  {formatMessage({
                    id: 'removeAddressHeading',
                    defaultMessage: 'Delete this address?',
                  })}
                </Text>
              </View>
            }
            bodyComponent={
              <View style={DialogStyles.bodyContainer}>
                <Text variant="body" textAlign="center">
                  {formatMessage({
                    id: 'removeAddressBody',
                    defaultMessage:
                      "Are you sure you want to remove it from your saved addresses? This action can't be undone.",
                  })}
                </Text>
              </View>
            }
            confirmButtonLabel={formatMessage({
              id: 'deleteButtonText',
              defaultMessage: 'Delete',
            })}
            confirmDialog={handleConfirmRemove}
            onDismiss={() => setShowRemoveDialog(false)}
            showCloseButton={true}
            closeButtonLabelId={formatMessage({
              id: 'keepAddress',
              defaultMessage: 'Keep Address',
            })}
            modalAppearanceEventMessage={formatMessage({
              id: 'RemoveDeliveryAddressConfirmation',
              defaultMessage: 'Remove Delivery Address Confirmation',
            })}
          />
        )}

        <ErrorDialog />
      </>
    );
  }
);
